import { lazy } from "react";
import { createBrowserRouter, RouterProvider, useRouteError } from "react-router-dom";

// Guards
import { ProtectedRoute } from "./guards/ProtectedRoute";
import { PublicOnlyRoute } from "./guards/PublicRoute";

// Layouts
import { MainLayout } from "../components/layouts/MainLayout";
import { AuthLayout } from "../components/layouts/AuthLayout";
import FormsLayout from "../components/form-builder/FormsLayout";
import Auth from "../pages/Auth";
import Tools from "../pages/ToolsPage";
import { Button, Stack, Typography } from "@mui/material";

// Lazy-loaded pages
const Map = lazy(() => import("../pages/Map"));
const Table = lazy(() => import("../pages/TablePage"));

const PageNotFound = lazy(() => import("../pages/PageNotFound"));
const NotAuthorizedPage = lazy(() => import("../pages/NotAuthorizedPage"));
const EditorPage = lazy(() => import("../pages/EditorPage"));
const UploadPage = lazy(() => import("../pages/UploadPage"));
const FormBuilderPage = lazy(() => import("../pages/FormBuilderPage"));
const FormImportReviewPage = lazy(() => import("../pages/FormImportReviewPage"));
const FormsListPage = lazy(() => import("../pages/FormsListPage"));
const ClassificationSetsPage = lazy(() => import("../pages/ClassificationSetsPage"));

// Error boundary component
const RouteErrorBoundary = () => {
  const error = useRouteError() as Error;
  return (
    <Stack
      sx={{
        height: "100dvh",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem"
      }}
    >
      <Stack gap='0.375rem'>
        <Typography variant='h1' fontWeight={600} color='grey.700'>
          Something went wrong
        </Typography>
        <Typography variant='body1' color='grey.500'>
          {error.message}
        </Typography>
      </Stack>
      <Button variant='contained' onClick={() => window.location.assign(window.location.origin)}>
        Go Home
      </Button>
    </Stack>
  );
};

// define main routes
const mainRoutes = [
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Map />
      </ProtectedRoute>
    )
  },
  {
    path: "map",
    element: (
      <ProtectedRoute>
        <Map />
      </ProtectedRoute>
    )
  },
  {
    path: "table",
    element: (
      <ProtectedRoute>
        <Table />
      </ProtectedRoute>
    )
  },
  {
    path: "editor",
    element: (
      <ProtectedRoute requiredPermissions={["canCreate", "canUpdate"]}>
        <EditorPage />
      </ProtectedRoute>
    )
  },
  {
    path: "upload",
    element: (
      <ProtectedRoute requiredPermissions={["canCreate"]}>
        <UploadPage />
      </ProtectedRoute>
    )
  },
  {
    path: "tools",
    element: (
      <ProtectedRoute>
        <Tools />
      </ProtectedRoute>
    )
  },
  {
    element: (
      <ProtectedRoute requiredPermissions={["canCreate", "canUpdate"]}>
        <FormsLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: "forms", element: <FormBuilderPage /> },
      { path: "forms/list", element: <FormsListPage /> },
      { path: "forms/classification-sets", element: <ClassificationSetsPage /> }
    ]
  },
  {
    path: "form-builder/:id",
    element: (
      <ProtectedRoute requiredPermissions={["canCreate", "canUpdate"]}>
        <FormBuilderPage />
      </ProtectedRoute>
    )
  },
  {
    path: "forms/import/:uuid",
    element: (
      <ProtectedRoute requiredPermissions={["canCreate", "canUpdate"]}>
        <FormImportReviewPage />
      </ProtectedRoute>
    )
  },
  {
    path: "unauthorized",
    element: <NotAuthorizedPage />
  },
  {
    path: "*",
    element: <PageNotFound />
  }
];

// define auth routes
const authRoutes = [
  {
    path: "login",
    element: (
      <PublicOnlyRoute>
        <Auth />
      </PublicOnlyRoute>
    )
  }
];

const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: authRoutes
  },
  {
    element: <MainLayout />,
    errorElement: <RouteErrorBoundary />,
    children: mainRoutes
  }
]);

export const Router: React.FC = () => {
  return <RouterProvider router={router} />;
};
